import React, { useEffect, useMemo, useState } from "react";
import { Table, Typography, Spin, Button, Modal, Space, Select, Descriptions, Tooltip } from "antd";
import ExpandCollapseAllButton from "../../common/ExpandCollapseAllButton";
import { InfoCircleOutlined, PlusOutlined, FolderAddOutlined, EditOutlined, DeleteOutlined, WarningOutlined } from "@ant-design/icons";
import NumberInput from "../../common/NumberInput";
import BcCalculationDetails, { computeBcCalculation, SummaryKeyRawFields } from "../../common/BcCalculationDetails";
import { useTender } from "../../../contexts/TenderContext";
import { useDevMode } from "@/contexts/DevModeContext";
import { useCanConfigureBoq } from "@/hooks/useCanConfigureBoq";
import { COLORS, formatUnit, MAX_SECTION_NESTING_DEPTH, MAX_SECTION_NESTING_DEPTH_MESSAGE, buildSectionDepthMap } from "../../../utils/constants";
import { useItemLibrary } from "../../../hooks/useItemLibrary";
import { useGlobalSettings } from "../../../hooks/useGlobalSettings";
import { createDrainageRateLookupMap } from "../../../utils/drainageItemLibraryIntegration";
import { getDrainageConfigureHeadersViolationsByField, type FieldConfigureHeadersViolation } from "../../../utils/groundworksConfigureHeadersValidation";
import {
  OSMA_FOUL_COMBINED_FIELDS,
  OSMA_STORM_COMBINED_FIELDS,
  POLYPIPE_FOUL_COMBINED_FIELDS,
  POLYPIPE_STORM_COMBINED_FIELDS,
} from "../../../utils/drainageReferenceMapping";
import { DRAINAGE_FIELD_LABELS } from "../../../utils/drainageFieldMapping";
import { useBoqLatestVersion } from "../../../hooks/useBillOfQuantities";
import { BoqSummaryLineItem, DrainageSummaryLineItem } from "../../../utils/groundworksBoqSummaryComputer";
import { computeDynamicBcCalculation, type DynamicBillSummaryLineItem } from '../../../utils/dynamicBillSummarySerialization';
import { useBillSummaryExtraItems, buildRootExtraRows, buildNestedExtraRows } from '../../../hooks/useBillSummaryExtraItems';
import { generatePlotMatrixCategoryColumns } from '../../../utils/plotMatrixCategoryGenerator';
import type { DynamicBillSummaryNode, DynamicBillSummaryItemNode, DynamicBillSummarySectionNode } from '../../../types/tender.types';

const findExtraNode = (nodes: DynamicBillSummaryNode[], id: string): DynamicBillSummaryNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.type === "section") {
      const found = findExtraNode(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

const { Text, Title } = Typography;

// Header/section rowTypes across this table — 'sub-header' is the only one the
// FIXED data ever produces; 'main-header'/'column-type-header' only ever come
// from ad-hoc extra Section rows (root-level / nested respectively — see
// useBillSummaryExtraItems.tsx's buildExtraRow depth convention), but every
// column below still has to treat all three the same way so a newly-added
// extra section renders consistently instead of falling through to
// leaf-row rendering.
const isHeaderRowType = (rowType: string) =>
  rowType === "sub-header" || rowType === "main-header" || rowType === "column-type-header";

// Exact column names from the Drainage table (groundworks/drainage), prefix stripped.
// Used only in the formula modal so the breakdown items match what the user sees in the table.
const FORMULA_MODAL_LABELS: Record<string, string> = {
  // OSMA Foul
  osma32mmBend: 'Lab per plot (Nr)',
  osmaPPIC: "PPIC's (Nr)",
  osma50mmBend: "Mini PPIC's (Nr)",
  osma68mmBend: 'SVP (Nr)',
  osma82mmBend: 'Concrete to rest bends (Nr)',
  osma110mmBend: '100/100 junctions (Nr)',
  osma32mmPipe: '100mm pipe (Nr)',
  osma40mmPipe: '100mm couplers (Nr)',
  osma50mmPipe: '100/150 junction (m)',
  osma68mmPipe: '150/150 junction (Nr)',
  osma82mmPipe: '150mm dia pipe (m)',
  osma110mmPipe: '150mm couplers (m)',
  gravel: 'Gravel (m³)',
  concealedSetSurround: 'Concrete bed & surround (m)',
  tipOnSite: 'Tip onsite (m³)',
  // OSMA Storm
  osmaStorm32mmBend: 'Lab per plot (Nr)',
  osmaStorPPIC: "PPIC's (Nr)",
  osmaStorm50mmBend: 'Rodding Eye (Nr)',
  osmaStorm68mmBend: 'RWP (m)',
  osmaStorm100100JunctionTripleSocket: '100/100 Junction (Triple Socket) (Nr)',
  osmaStorm82mmBend: '100mm pipe (Nr)',
  osmaStorm110mmBend: '100mm couplers (Nr)',
  osmaStorm32mmPipe: '100/150 junction (Nr)',
  osmaStorm40mmPipe: '150/150 junction (m)',
  osmaStorm50mmPipe: '150mm dia pipe (Nr)',
  osmaStorm68mmPipe: '150mm couplers (m)',
  osmaStormGravel: 'Gravel (m)',
  osmaStormConcreteBedSurround: 'Concrete bed & surround (m)',
  osmaStormTipOnsite: 'Tip onsite (m³)',
  // Polypipe Foul
  polypipe32mmBend: 'Lab per plot (Nr)',
  polypipePPIC: "PPIC's (Nr)",
  polypipe50mmBend: "Mini PPIC's (Nr)",
  polypipe68mmBend: 'SVP (Nr)',
  polypipe82mmBend: 'Concrete to rest bends (Nr)',
  polypipe110mmBend: '100/100 Junctions (Nr)',
  polypipe32mmPipe: '100mm pipe (m)',
  polypipe40mmPipe: '100mm couplers (Nr)',
  polypipe50mmPipe: '100/150 junction (Nr)',
  polypipe68mmPipe: '150/150 junction (Nr)',
  polypipe82mmPipe: '150mm dia pipe (m)',
  polypipe110mmPipe: '150mm couplers (Nr)',
  polypipeGravel: 'Gravel (m)',
  polypipeConcreteBedSurround: 'Concrete bed & surround (m)',
  polypipeTipOnSite: 'Tip onsite (m³)',
  // Polypipe Storm
  polypipeStorm32mmBend: 'Lab per plot (Nr)',
  polypipeStormPPIC: "PPIC's (Nr)",
  polypipeStorm50mmBend: 'Rodding Eye (Nr)',
  polypipeStorm68mmBend: 'RWP (m)',
  polypipeStorm100100JunctionTripleSocket: '100/100 Junction (Triple Socket) (Nr)',
  polypipeStorm82mmBend: '100mm pipe (Nr)',
  polypipeStorm110mmBend: '100mm couplers (Nr)',
  polypipeStorm32mmPipe: '100/150 junction (Nr)',
  polypipeStorm40mmPipe: '150/150 junction (m)',
  polypipeStorm50mmPipe: '150mm dia pipe (Nr)',
  polypipeStorm68mmPipe: '150mm couplers (m)',
  polypipeStormGravel: 'Gravel (m)',
  polypipeStormConcreteBedSurround: 'Concrete bed & surround (m³)',
  polypipeStormTipOnSite: 'Tip onsite (m³)',
};

// Type for the per-field breakdown rows the formula modal needs per per-plot
// section. Each entry corresponds to one "combined" field that contributes to
// the section's `Σ(qty × rate)` numerator.
interface PerPlotFormulaBreakdown {
  field: string;
  label: string;
  qty: number;
  rate: number;
}

const DrainageBillSummary: React.FC = () => {
  const showDevMode = useDevMode();
  // Ad-hoc "Add Item"/"Add Section" extra rows — gated by the "Configure BOQ" switch (next to the Re-calculate
  // button on /groundworks/review), independent of Dev Mode (useCanConfigureBoq(), hooks/useCanConfigureBoq.ts) — same
  // convention as GroundworksCutFillBillSummary.tsx / FoundationsBillSummary.tsx.
  const dynamicSummaryEnabled = useCanConfigureBoq();
  const { state, updateField } = useTender();
  const { settings } = useGlobalSettings();
  const { data: itemLibraryData, isLoading: isLoadingItemLibrary } =
    useItemLibrary(state.region || "");

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  // Holds the row.key whose formula modal is currently open (`"1.0"`, `"2.0"`
  // — only the per-plot rows of each section have a formula icon). `null` =
  // closed.
  const [formulaInfoKey, setFormulaInfoKey] = useState<string | null>(null);
  const [summaryKeyModalRecord, setSummaryKeyModalRecord] = useState<DrainageSummaryLineItem | null>(null);
  const [configViolationModalRecord, setConfigViolationModalRecord] = useState<FieldConfigureHeadersViolation | null>(null);
  const configViolationsByField = useMemo(
    () => getDrainageConfigureHeadersViolationsByField(state.groundworks),
    [state.groundworks],
  );
  // SummaryKey modal for ad-hoc extra items — these have no persisted boq-versions
  // entry (`apiSummaryItem`) since they aren't serialised into the exported BOQ
  // payload yet, so this shows the live-derived value instead, same "unsaved"
  // convention DynamicBillSummaryPanel's own SummaryKey column uses.
  // `persisted` (when set) is the actual raw `DrainageSummaryLineItem` fetched
  // from the BOQ API for this extra item's own `key` — shown in full via
  // `SummaryKeyRawFields` so the modal reflects every detail the API actually
  // has, not just the hand-picked subset of `live`'s fields. `live` (the
  // client-side node, always set) still drives the BCCalculation breakdown,
  // since that derivation needs the node's own `resourceCodes` array — a
  // persisted record only ever carries the combined `resourceCode` string.
  const [extraSummaryKeyRecord, setExtraSummaryKeyRecord] = useState<{
    live: DynamicBillSummaryLineItem;
    persisted: DrainageSummaryLineItem | null;
  } | null>(null);

  // Ad-hoc user-added Section/Item rows grafted onto this otherwise-immutable
  // computed table — see useBillSummaryExtraItems.tsx. Gated by
  // dynamicSummaryEnabled (the "Configure BOQ" switch), same as Cut & Fill's /
  // Foundations' own extra items. `drainageExtraItems` is keyed by
  // "attachment point": `"__root__"` for new top-level rows, or an existing
  // computed section row's own `key` for rows grafted onto it.
  const drainageExtraItems = state.groundworks?.drainageExtraItems;
  // Live Plot Matrix columns, for the Add/Edit Item modal's "Include in Plot
  // Matrix" Valuation Stage lookup — same call GroundworksCutFillBillSummary.tsx
  // makes for its own extra items.
  const extraItemsPlotMatrixColumns = useMemo(
    () => generatePlotMatrixCategoryColumns(state.groundworks as any),
    [state.groundworks],
  );
  // Newly-added extra row (Section or Item) — auto-expands the row it was
  // grafted onto and briefly highlights/autofocuses it, same convention
  // GroundworksCutFillBillSummary.tsx follows for its own Add Section/Add Item.
  const [newlyAddedRowId, setNewlyAddedRowId] = useState<string | null>(null);
  useEffect(() => {
    if (!newlyAddedRowId) return;
    const timer = setTimeout(() => setNewlyAddedRowId(null), 2000);
    return () => clearTimeout(timer);
  }, [newlyAddedRowId]);

  const extraItemsApi = useBillSummaryExtraItems({
    extraItems: drainageExtraItems,
    onChange: (updated) =>
      updateField("groundworks", { ...state.groundworks, drainageExtraItems: updated }),
    itemLibraryData,
    plotMatrixColumns: extraItemsPlotMatrixColumns,
    onNodeAdded: (expandKey, nodeId) => {
      if (expandKey) {
        setExpandedRowKeys((prev) => (prev.includes(expandKey) ? prev : [...prev, expandKey]));
      }
      setNewlyAddedRowId(nodeId);
    },
  });

  // Dev-mode only: pulls the persisted `groundworks.summary.drainage` line items from the
  // same boq-versions API (`getBoqVersion`) that drives the rest of the review page, so the
  // SummaryKey column reflects what was actually saved rather than the live recomputation
  // below. Unlike the other bill summaries, drainage rows are matched by `key` ("1.0", "1.1"…)
  // rather than `fieldName`, since that's what `computeDrainageSection` keys its rows by too —
  // ad-hoc extra items' own `key` (== their `fieldName`) hits this same map correctly.
  const { data: latestBoqVersionData } = useBoqLatestVersion(
    state.projectName || undefined,
    { enabled: showDevMode && !!state.projectName, oppNo: state.opportunityNo },
  );

  const apiDrainageSummaryByKey = useMemo(() => {
    const map = new Map<string, DrainageSummaryLineItem>();
    const items = (latestBoqVersionData as unknown as { boq?: { billOfQuantities?: Array<{ groundworks?: { summary?: { drainage?: DrainageSummaryLineItem[] } } }> } })
      ?.boq?.billOfQuantities?.[0]?.groundworks?.summary?.drainage;
    (items || []).forEach((item) => {
      if (item.key) map.set(item.key, item);
    });
    return map;
  }, [latestBoqVersionData]);

  // Build a code→{rate} lookup directly from item library, for the SummaryKey modal's
  // "BCCalculation" line (same convention as GroundworksCutFillBillSummary).
  const codeRateLookup = useMemo(() => {
    const map = new Map<string, { rate: number }>();
    if (!itemLibraryData) return map;
    itemLibraryData.forEach((item) => {
      const code = item.resourceCode?.trim().toUpperCase();
      if (code) {
        map.set(code, { rate: item.nettRate || item.baseRate || 0 });
      }
    });
    return map;
  }, [itemLibraryData]);

  const rateLookupMap = useMemo(() => {
    if (!itemLibraryData) return new Map();
    return createDrainageRateLookupMap(itemLibraryData, state.groundworks);
  }, [itemLibraryData, state.groundworks]);

  const processedData: any[] = useMemo(() => {
    const markupFactor = (1 + settings.groundworks.overheadAndProfit / 100) * (1 + settings.groundworks.preliminaries / 100);

    const drainageRows: any[] =
      (state.groundworks as any)?.drainageTableStandard ||
      (state.groundworks as any)?.drainageTable ||
      [];
    const osmaConfig = (state.groundworks as any)?.drainageDimensionValues || {};
    const polypipeConfig = (state.groundworks as any)?.drainagePolypipeConfig || {};

    const sumField = (field: string): number =>
      drainageRows.reduce((acc, row) => acc + (parseFloat(row[field]) || 0), 0);

    const getRateInfo = (fieldName: string) => rateLookupMap.get(fieldName);
    const getRate = (fieldName: string): number => getRateInfo(fieldName)?.rate || 0;

    const hasOsma = drainageRows.some((row: any) => (parseFloat(row.osma32mmBend) || 0) > 0 || (parseFloat(row.osmaStorm32mmBend) || 0) > 0);
    const hasPolypipe = drainageRows.some((row: any) => (parseFloat(row.polypipe32mmBend) || 0) > 0 || (parseFloat(row.polypipeStorm32mmBend) || 0) > 0);
    const usePolypipe = hasPolypipe && !hasOsma;

    const buildPerPlotRow = (
      key: string,
      fallbackDescription: string,
      labField: string,
      combinedFields: readonly string[],
      totalPlots: number,
      nrPlotsTakeOff: number,
    ) => {
      // Per-plot rate = Σ_field (sumField(field) × itemLibraryRate(field)) ÷
      // nrPlotsTakeOff, summed over every "combined" field for this section
      // (Foul – OSMA, Storm – OSMA, Foul – Polypipe, Storm – Polypipe).
      // The "separate" items (NME PPIC / 600mm NME PPIC / Yard Gully /
      // Drainage Channel / Land Drain) keep their own rows below and are
      // excluded from this sum.
      //
      // Build the per-field breakdown alongside the sum so the formula
      // modal can render the exact items the user is computing against
      // without re-deriving them.
      const breakdown: PerPlotFormulaBreakdown[] = combinedFields.map((field) => {
        const qty = sumField(field);
        const fieldRate = getRate(field);
        return {
          field,
          label: FORMULA_MODAL_LABELS[field] ?? DRAINAGE_FIELD_LABELS[field] ?? getRateInfo(field)?.description ?? field,
          qty,
          rate: fieldRate,
        };
      });
      const combinedSum = breakdown.reduce((sum, b) => sum + b.qty * b.rate, 0);
      const baseRate = nrPlotsTakeOff > 0 ? combinedSum / nrPlotsTakeOff : 0;
      const rate = baseRate * markupFactor;
      const roundedPlots = Math.round(totalPlots);
      const amount = roundedPlots * parseFloat(rate.toFixed(2));
      const description = "Plot Drainage";
      const resourceCode = getRateInfo(labField)?.resourceCode || '';
      const libraryRate = getRate(labField);
      return {
        key,
        category: key,
        description,
        quantity: String(roundedPlots),
        unit: "plot",
        rate: rate.toFixed(2),
        amount: amount.toFixed(2),
        rowType: "line-item",
        hasData: true,
        resourceCode,
        dbRate: libraryRate.toFixed(4),
        // Formula-modal inputs. Presence of `formulaBreakdown` is the marker
        // the description-column renderer uses to decide whether to show the
        // info icon.
        formulaBreakdown: breakdown,
        formulaCombinedSum: combinedSum,
        formulaNrPlotsTakeOff: nrPlotsTakeOff,
        formulaTotalPlots: roundedPlots,
        formulaSectionLabel: "Plot Drainage",
        apiSummaryItem: apiDrainageSummaryByKey.get(key) || null,
        configViolation: configViolationsByField.get(labField) ?? null,
      };
    };

    const buildItemRow = (key: string, fallbackDescription: string, fieldName: string, unit: string, quantityOverride?: number) => {
      const quantity = quantityOverride !== undefined ? quantityOverride : sumField(fieldName);
      const rateInfo = getRateInfo(fieldName);
      const baseRate = rateInfo?.rate || 0;
      const rate = baseRate * markupFactor;
      const description = rateInfo?.description || fallbackDescription;
      const resolvedUnit = rateInfo?.unit || unit;
      const roundedQty = Math.round(quantity);
      const amount = roundedQty * parseFloat(rate.toFixed(2));
      const resourceCode = rateInfo?.resourceCode || '';
      return { key, category: key, description, quantity: String(roundedQty), unit: resolvedUnit, rate: rate.toFixed(2), amount: amount.toFixed(2), rowType: "line-item", hasData: true, resourceCode, dbRate: baseRate.toFixed(4), apiSummaryItem: apiDrainageSummaryByKey.get(key) || null, configViolation: configViolationsByField.get(fieldName) ?? null };
    };

    const buildSection = (sectionKey: string, displayItem: string, label: string, children: any[]) => {
      const sectionAmount = children.reduce((sum, row) => sum + parseFloat(row.amount || 0), 0);
      return {
        key: sectionKey,
        category: displayItem,
        description: label.toUpperCase(),
        quantity: "",
        unit: "",
        rate: "",
        amount: "",
        rowType: "sub-header",
        isDiameterHeader: true,
        children,
        sectionAmount,
      };
    };

    // Drop rows where the rounded quantity is zero — matches the export and other
    // bill summaries which suppress no-data items.
    const filterNonZero = (items: any[]) =>
      items.filter(item => Math.round(parseFloat(String(item.quantity || 0))) !== 0);

    const fixedSections: any[] = usePolypipe
      ? (() => {
          const ppTotalPlots = parseFloat(polypipeConfig.totalPlots) || 0;
          const ppNrPlotsTakeOff = parseFloat(polypipeConfig.nrPlotsTakeOff) || 0;
          return [
            buildSection("section-1", "1.0", "Polypipe-Foul", filterNonZero([
              buildPerPlotRow("1.0", "Foul – Polypipe", "polypipe32mmBend", POLYPIPE_FOUL_COMBINED_FIELDS, ppTotalPlots, ppNrPlotsTakeOff),
              buildItemRow("1.1", "NME PPIC",       "polypipeNmePpic",        "Nr"),
              buildItemRow("1.2", "600mm NME PPIC", "polypipe600mmNmePpic",   "Nr"),
            ])),
            buildSection("section-2", "2.0", "Polypipe-Storm", filterNonZero([
              buildPerPlotRow("2.0", "Storm – Polypipe", "polypipeStorm32mmBend", POLYPIPE_STORM_COMBINED_FIELDS, ppTotalPlots, ppNrPlotsTakeOff),
              buildItemRow("2.1", "NME PPIC",       "polypipeStormNmePpic",      "Nr"),
              buildItemRow("2.2", "600mm NME PPIC", "polypipeStorm600mmNmePpic", "Nr"),
              buildItemRow("2.3", "Yard Gully",           "polypipeStormYardGully",    "Nr", sumField("polypipeStormYardGully") || sumField("yardGully")),
              buildItemRow("2.4", "Drainage Channel",     "polypipeStormDrainageChannel",  "m",  sumField("polypipeStormDrainageChannel") || sumField("drainageChannel")),
              buildItemRow("2.5", "Land Drain",           "polypipeStormLandDrain",        "m",  sumField("polypipeStormLandDrain") || sumField("landDrain")),
            ])),
          ].filter(section => section.children.length > 0);
        })()
      : (() => {
          const osmaTotalPlots = parseFloat(osmaConfig.foulTotalPlots) || 0;
          const osmaNrPlotsTakeOff = parseFloat(osmaConfig.foulNrPlotsTakeOff) || 0;
          return [
            buildSection("section-1", "1.0", "OSMA-Foul", filterNonZero([
              buildPerPlotRow("1.0", "Foul – OSMA", "osma32mmBend", OSMA_FOUL_COMBINED_FIELDS, osmaTotalPlots, osmaNrPlotsTakeOff),
              buildItemRow("1.1", "NME PPIC",       "osmaNmePpic",        "Nr"),
              buildItemRow("1.2", "600mm NME PPIC", "osma600mmNmePpic",   "Nr"),
            ])),
            buildSection("section-2", "2.0", "OSMA-Storm", filterNonZero([
              buildPerPlotRow("2.0", "Storm – OSMA", "osmaStorm32mmBend", OSMA_STORM_COMBINED_FIELDS, osmaTotalPlots, osmaNrPlotsTakeOff),
              buildItemRow("2.1", "NME PPIC",       "osmaStormNmePpic",      "Nr"),
              buildItemRow("2.2", "600mm NME PPIC", "osmaStorm600mmNmePpic", "Nr"),
              buildItemRow("2.3", "Yard Gully",           "osmaStormYardGully",    "Nr", sumField("osmaStormYardGully") || sumField("yardGully")),
              buildItemRow("2.4", "Drainage Channel",     "osmaStormDrainageChannel", "m", sumField("osmaStormDrainageChannel") || sumField("drainageChannel")),
              buildItemRow("2.5", "Land Drain",           "osmaStormLandDrain",    "m",  sumField("osmaStormLandDrain") || sumField("landDrain")),
            ])),
          ].filter(section => section.children.length > 0);
        })();

    // Graft ad-hoc extra rows on top of this otherwise-immutable computed tree —
    // nested additions merge onto the matching existing section row (by
    // `key`), and root additions become new top-level sections continuing on
    // from however many fixed sections actually survived the zero-filter
    // above (1 or 2, or 0 for a project with no drainage data at all). Same
    // convention GroundworksCutFillBillSummary.tsx / FoundationsBillSummary.tsx follow.
    const extraBuckets = drainageExtraItems ?? {};
    // Buckets whose attach-point row is actually present in this render's
    // computed tree — anything left over (see orphanedExtraRows below) had
    // its row disappear (e.g. Drainage take-off data was emptied, dropping
    // every fixed section it was nested under) and must not be silently lost.
    const consumedAttachKeys = new Set<string>();
    const injectExtraRows = (rows: any[]): any[] =>
      rows.map((row) => {
        const bucket = extraBuckets[row.key];
        const existingChildren: any[] = row.children ?? [];
        const ownChildren = row.children ? injectExtraRows(row.children) : undefined;
        if (!bucket || bucket.length === 0) {
          return ownChildren ? { ...row, children: ownChildren } : row;
        }
        consumedAttachKeys.add(row.key);
        const extraRows = buildNestedExtraRows(bucket, row.key, row.category, existingChildren.length, markupFactor);
        const mergedChildren = [...(ownChildren ?? []), ...extraRows];
        return {
          ...row,
          children: mergedChildren,
          ...(row.sectionAmount !== undefined && {
            sectionAmount: mergedChildren.reduce((sum, c) => sum + (parseFloat(c.amount || "0") || 0), 0),
          }),
        };
      });
    const injectedResult = injectExtraRows(fixedSections);
    let nextRootNumber = fixedSections.length + 1;
    const rootExtraRows = buildRootExtraRows(extraBuckets["__root__"] ?? [], "__root__", nextRootNumber, markupFactor);
    nextRootNumber += rootExtraRows.length;
    // Orphaned nested buckets (attach-point row no longer exists — e.g. its
    // parent section disappeared) wrap under a single synthetic "Additional
    // Items" header instead of landing as bare unlabelled rows — not a real
    // node itself (Edit/Delete never targets it, only its real children
    // match a node id), just keyed by the REAL attachKey so those children's
    // Edit/Delete/Qty edits still address the correct bucket in state.
    const orphanedExtraRows = Object.keys(extraBuckets)
      .filter((key) => key !== "__root__" && !consumedAttachKeys.has(key) && extraBuckets[key].length > 0)
      .map((key) => {
        const n = nextRootNumber++;
        const children = buildNestedExtraRows(extraBuckets[key], key, `${n}.0`, 0, markupFactor);
        return {
          key: `orphaned-${key}`,
          category: `${n}.0`,
          description: "Additional Items",
          rowType: "main-header",
          isExtra: true,
          attachKey: key,
          nodeType: "section",
          quantity: "",
          unit: "",
          rate: "",
          amount: "",
          dbRate: "",
          sectionAmount: children.reduce((sum, c) => sum + (parseFloat(c.amount || "0") || 0), 0),
          children,
        };
      });

    return [...injectedResult, ...rootExtraRows, ...orphanedExtraRows];
  }, [state.groundworks, rateLookupMap, settings.groundworks.preliminaries, settings.groundworks.overheadAndProfit, apiDrainageSummaryByKey, drainageExtraItems, configViolationsByField]);

  // Depth of every row (real category rows AND grafted extra rows alike) in
  // the merged `processedData` tree — gates a section row's "Add Section"
  // Actions icon at MAX_SECTION_NESTING_DEPTH, counting pre-existing real
  // category nesting as well as already-added extra sections.
  const rowDepthMap = useMemo(() => buildSectionDepthMap(processedData), [processedData]);

  if (isLoadingItemLibrary) {
    return (
      <div style={{ padding: "16px", textAlign: "center" }}>
        <Spin size="large" />
        <div style={{ marginTop: "16px" }}>
          <Text className="text-gray-500">Loading item library rates...</Text>
        </div>
      </div>
    );
  }

  const flattenAll = (items: any[]): any[] => {
    let result: any[] = [];
    items.forEach((item) => {
      if (item.rowType === "line-item") {
        result.push(item);
      }
      if (item.children?.length) {
        result.push(...flattenAll(item.children));
      }
    });
    return result;
  };

  const totalAmount = flattenAll(processedData).reduce(
    (sum, item) => sum + parseFloat(item.amount || 0),
    0
  );

  return (
    <div style={{ padding: "16px" }}>
      <div style={{ marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Title level={5} style={{ margin: 0, fontWeight: 600, fontSize: "16px", color: "#111827" }}>
          Drainage Bill Summary
        </Title>
        <Space>
          {dynamicSummaryEnabled && !state.isViewMode && (
            <>
              <Button size="small" icon={<FolderAddOutlined />} onClick={() => extraItemsApi.openAddSection("__root__", null)}>
                Add Section
              </Button>
              {/* Root-level "Add Item" button hidden per request (2026-09-01) — row-level Add Item icons remain.
              <Button size="small" icon={<PlusOutlined />} onClick={() => extraItemsApi.openAddItem("__root__", null)}>
                Add Item
              </Button>
              */}
            </>
          )}
          <ExpandCollapseAllButton
            expanded={expandedRowKeys.length > 0}
            onToggle={() => {
              if (expandedRowKeys.length > 0) {
                setExpandedRowKeys([]);
              } else {
                const allKeys: React.Key[] = [];
                const collect = (rows: any[]) => {
                  rows.forEach((row) => {
                    if (row.children?.length) {
                      allKeys.push(row.key);
                      collect(row.children);
                    }
                  });
                };
                collect(processedData);
                setExpandedRowKeys(allKeys);
              }
            }}
          />
        </Space>
      </div>

      <style>
        {`
          .drainage-summary-table .ant-table {
            border-radius: 0 !important;
          }
          .drainage-summary-table .ant-table-container {
            border-radius: 0 !important;
          }
        `}
      </style>

      <Table
        className="drainage-summary-table"
        dataSource={processedData}
        expandable={{
          childrenColumnName: "children",
          defaultExpandAllRows: false,
          expandedRowKeys,
          onExpand: (expanded, record) => {
            if (expanded) {
              setExpandedRowKeys([...expandedRowKeys, record.key]);
            } else {
              setExpandedRowKeys(expandedRowKeys.filter((key) => key !== record.key));
            }
          },
        }}
        columns={[
          {
            title: "Item",
            dataIndex: "category",
            key: "category",
            width: 110,
            align: "center",
            render: (value, record) => {
              if (isHeaderRowType(record.rowType)) {
                return <Text strong>{value}</Text>;
              }
              return value;
            },
          },
          {
            title: "Description",
            dataIndex: "description",
            key: "description",
            width: 350,
            render: (value, record) => {
              if (isHeaderRowType(record.rowType)) {
                return <Text strong className="text-indigo-600">{value}</Text>;
              }
              const hasFormulaBreakdown = Array.isArray((record as any).formulaBreakdown);
              return (
                <div>
                  <Text>{value}</Text>
                  {showDevMode && hasFormulaBreakdown && (
                    <InfoCircleOutlined
                      style={{ color: '#1677ff', marginLeft: 6, cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormulaInfoKey((record as any).key);
                      }}
                    />
                  )}
                  {(record as any).configViolation && (
                    <Tooltip title="Configure Headers required — click for details">
                      <WarningOutlined
                        style={{ color: '#faad14', marginLeft: 6, cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfigViolationModalRecord((record as any).configViolation);
                        }}
                      />
                    </Tooltip>
                  )}
                  {showDevMode && (record.resourceCode || (record.resourceCodes && record.resourceCodes.length > 0)) && (
                    <div style={{ fontSize: 10, color: '#999', fontWeight: 500, marginTop: 2 }}>
                      {record.resourceCode || record.resourceCodes!.map((c: { resourceCode: string; operator: '+' | '-' }, i: number) => (i === 0 ? c.resourceCode : `${c.operator} ${c.resourceCode}`)).join(' ')}
                    </div>
                  )}
                </div>
              );
            },
          },
          {
            title: "Qty",
            dataIndex: "quantity",
            key: "quantity",
            width: 110,
            align: "right",
            render: (value, record) => {
              if (isHeaderRowType(record.rowType)) return "";
              // Ad-hoc extra items are directly editable in-cell, same as
              // GroundworksCutFillBillSummary.tsx's own Qty column — existing
              // computed rows stay read-only. Text amount type has no Qty at
              // all, same convention DynamicBillSummaryPanel's Qty column follows.
              if (record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
                return (
                  <NumberInput
                    value={parseFloat(value) || 0}
                    onChange={(qty) => extraItemsApi.updateNode(record.attachKey, record.key, { qty })}
                    size="small"
                    decimalPlaces={2}
                    autoFocus={record.key === newlyAddedRowId}
                  />
                );
              }
              if (!value || value === "" || value === "0" || value === "0.00") return "";
              const numValue = typeof value === "number" ? value : parseFloat(value);
              return isNaN(numValue) ? "" : <div style={{ textAlign: "right" }}>{Math.round(numValue).toLocaleString("en-GB")}</div>;
            },
          },
          {
            title: "Unit",
            dataIndex: "unit",
            key: "unit",
            width: 110,
            align: "center",
            render: (value: string, record) => {
              if (isHeaderRowType(record.rowType)) return "";
              // Same in-cell Select every extra-items Unit column uses.
              // Text amount type has no Unit at all.
              if (record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
                return (
                  <Select
                    value={value || undefined}
                    onChange={(unit) => extraItemsApi.updateNode(record.attachKey, record.key, { unit })}
                    options={extraItemsApi.unitOptions.map((u) => ({ value: u, label: u }))}
                    placeholder="Unit"
                    size="small"
                    showSearch
                    allowClear
                    style={{ width: "100%" }}
                  />
                );
              }
              return formatUnit(value);
            },
          },
          {
            title: "Rate",
            dataIndex: "rate",
            key: "rate",
            width: 110,
            align: "right",
            render: (value, record) => {
              if (isHeaderRowType(record.rowType)) return "";
              if (!value || value === "" || value === "0" || value === "0.00") return "";
              const numValue = typeof value === "number" ? value : parseFloat(value);
              return isNaN(numValue) ? "" : <div style={{ textAlign: "right" }}>£{numValue.toFixed(2)}</div>;
            },
          },
          ...(showDevMode ? [{
            title: 'DB Rate',
            dataIndex: 'dbRate',
            key: 'dbRate',
            width: 100,
            align: 'right' as const,
            render: (value: string, record: any) => {
              if (isHeaderRowType(record.rowType)) return '';
              if (!value || value === '') return '';
              const numValue = parseFloat(value);
              if (isNaN(numValue) || numValue === 0) return '';
              return <div style={{ textAlign: 'right', color: '#9ca3af', fontSize: '12px' }}>£{numValue.toFixed(4)}</div>;
            },
          }] : []),
          {
            title: "Amount",
            dataIndex: "amount",
            key: "amount",
            width: 120,
            align: "right",
            render: (value, record) => {
              if (isHeaderRowType(record.rowType) && record.children?.length > 0) {
                const isExpanded = expandedRowKeys.includes(record.key);
                if (!isExpanded && record.sectionAmount > 0) {
                  return (
                    <div style={{ textAlign: "right" }}>
                      <span className="font-semibold">
                        £{record.sectionAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  );
                }
                return "";
              }
              // Text amount type (extra items only) — a free-typed value like
              // "Included"/"Rate Only" instead of a computed £ amount, same
              // convention DynamicBillSummaryPanel's own Amount column uses.
              if (record.amountType === 'text') {
                return <div style={{ textAlign: 'right' }}><Text>{record.amountText}</Text></div>;
              }
              if (!value || value === "0.00") return "";
              return (
                <div style={{ textAlign: "right" }}>
                  <Text strong>
                    £{parseFloat(value).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Text>
                </div>
              );
            },
          },
          ...(showDevMode ? [{
            title: 'SummaryKey',
            dataIndex: 'apiSummaryItem',
            key: 'summaryKey',
            width: 150,
            align: 'right' as const,
            render: (value: DrainageSummaryLineItem | null | undefined, record: any) => {
              if (isHeaderRowType(record.rowType)) return '';
              if (record.isExtra) {
                const node = findExtraNode(drainageExtraItems?.[record.attachKey] ?? [], record.key) as DynamicBillSummaryItemNode | null;
                if (!node) return '';
                const liveAmount = parseFloat(String(record.amount ?? '0')) || 0;
                const lineItem: DynamicBillSummaryLineItem = {
                  key: node.id,
                  description: node.description,
                  quantity: node.qty,
                  unit: node.unit,
                  rate: node.rate,
                  amount: liveAmount,
                  amountType: node.amountType,
                  amountText: node.amountText,
                  resourceCode: node.resourceCode,
                  resourceCodes: node.resourceCodes,
                };
                const bc = computeDynamicBcCalculation(lineItem, (code) => codeRateLookup.get(code)?.rate ?? 0);
                // Once saved, this extra item persists into the real BOQ document's
                // `summary.drainage` array under its own `key`/`id` (see
                // flattenDrainageExtraItemsToBoqLines, dynamicBillSummarySerialization.ts)
                // — same persisted-vs-live comparison every other row's SummaryKey
                // shows. Before the first save, there's nothing to compare yet, so
                // this falls back to a live-derived "unsaved" value instead — same
                // convention DynamicBillSummaryPanel's own SummaryKey column uses.
                const persisted = apiDrainageSummaryByKey.get(record.key);
                const persistedAmount = persisted ? parseFloat(String(persisted.amount || 0)) : liveAmount;
                const mismatch = !!persisted && Math.abs(liveAmount - persistedAmount) > 0.01;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Text style={{ fontSize: 12, color: mismatch ? '#dc2626' : persisted ? '#6b7280' : '#f59e0b' }}>
                        £{persistedAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Text>
                      <InfoCircleOutlined
                        style={{ cursor: 'pointer', color: '#3b82f6' }}
                        onClick={() => setExtraSummaryKeyRecord({ live: lineItem, persisted: persisted ?? null })}
                      />
                    </div>
                    {bc && (
                      <Text style={{ fontSize: 10, color: mismatch ? '#dc2626' : '#3b82f6' }}>
                        BC: £{bc.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Text>
                    )}
                    {!persisted && <Text style={{ fontSize: 10, color: '#f59e0b' }}>unsaved</Text>}
                  </div>
                );
              }
              if (!value) return '';
              const bcAmount = computeBcCalculation(value as unknown as BoqSummaryLineItem, (code) => codeRateLookup.get(code)?.rate ?? 0).amount;
              const liveAmount = parseFloat(String(record.amount ?? '0')) || 0;
              const mismatch = Math.abs(liveAmount - bcAmount) > 0.01;
              return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Text style={{ fontSize: 12, color: mismatch ? '#dc2626' : '#6b7280' }}>
                      £{parseFloat(String(value.amount || 0)).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Text>
                    <InfoCircleOutlined
                      style={{ cursor: 'pointer', color: '#3b82f6' }}
                      onClick={() => setSummaryKeyModalRecord(value)}
                    />
                  </div>
                  <Text style={{ fontSize: 10, color: mismatch ? '#dc2626' : '#3b82f6' }}>
                    BC: £{bcAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Text>
                </div>
              );
            },
          }] : []),
          ...(dynamicSummaryEnabled && !state.isViewMode ? [{
            title: 'Actions',
            key: 'extraActions',
            width: 150,
            align: 'center' as const,
            render: (_: unknown, record: any) => {
              if (record.isExtra) {
                const node = findExtraNode(drainageExtraItems?.[record.attachKey] ?? [], record.key);
                if (!node) return '';
                if (record.nodeType === 'section') {
                  return (
                    <Space size={4}>
                      <Tooltip title={(rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH ? MAX_SECTION_NESTING_DEPTH_MESSAGE : undefined}>
                        <Button
                          size="small"
                          icon={<FolderAddOutlined />}
                          title="Add Section"
                          disabled={(rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH}
                          onClick={() => extraItemsApi.openAddSection(record.attachKey, record.key)}
                        />
                      </Tooltip>
                      <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(record.attachKey, record.key)} />
                      <Button size="small" icon={<EditOutlined />} title="Edit Section" onClick={() => extraItemsApi.openEditSection(record.attachKey, node as DynamicBillSummarySectionNode)} />
                      <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(record.attachKey, record.key)} />
                    </Space>
                  );
                }
                return (
                  <Space size={4}>
                    <Button size="small" icon={<EditOutlined />} title="Edit Item" onClick={() => extraItemsApi.openEditItem(record.attachKey, node as DynamicBillSummaryItemNode)} />
                    <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(record.attachKey, record.key)} />
                  </Space>
                );
              }
              // Existing computed row — immutable, but a section row can host
              // grafted extra rows. A plain computed line-item gets no Actions
              // at all — only sections are attachment points.
              if (!isHeaderRowType(record.rowType)) {
                return '';
              }
              {
                const atMaxDepth = (rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH;
                return (
                  <Space size={4}>
                    <Tooltip title={atMaxDepth ? MAX_SECTION_NESTING_DEPTH_MESSAGE : undefined}>
                      <Button
                        size="small"
                        icon={<FolderAddOutlined />}
                        title="Add Section"
                        disabled={atMaxDepth}
                        onClick={() => extraItemsApi.openAddSection(record.key, null)}
                      />
                    </Tooltip>
                    <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(record.key, null)} />
                  </Space>
                );
              }
            },
          }] : []),
        ]}
        pagination={false}
        size="middle"
        bordered
        rowClassName={(record) => {
          const classes: string[] = [];
          if (isHeaderRowType(record.rowType)) classes.push('bg-indigo-50', 'font-semibold');
          if (record.key === newlyAddedRowId) classes.push('drainage-summary-row-added');
          return classes.join(' ');
        }}
        summary={(pageData) => {
          const flat = flattenAll(pageData as any[]);
          const totalAmt = flat.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
          return (
            <Table.Summary.Row style={{ backgroundColor: "#fafafa", fontWeight: 700 }}>
              <Table.Summary.Cell index={0} colSpan={2}>
                <Text strong style={{ fontWeight: 700 }}>TOTAL DRAINAGE</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} />
              <Table.Summary.Cell index={3} />
              <Table.Summary.Cell index={4} />
              {showDevMode && <Table.Summary.Cell index={5} />}
              <Table.Summary.Cell index={showDevMode ? 6 : 5} align="right">
                <span style={{ display: "block", textAlign: "right", fontWeight: 700 }}>
                  £{totalAmt.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </Table.Summary.Cell>
              {showDevMode && <Table.Summary.Cell index={7} />}
              {dynamicSummaryEnabled && !state.isViewMode && <Table.Summary.Cell index={8} />}
            </Table.Summary.Row>
          );
        }}
      />

      <style>{`
        .drainage-summary-table .ant-table-thead > tr > th {
          min-width: 110px !important;
          background-color: ${COLORS.tableHeaderBg} !important;
        }
        /* Newly-added extra Section/Item row (see newlyAddedRowId) — a brief
           highlight fade so the row is easy to spot the moment its parent
           auto-expands to reveal it. */
        @keyframes drainage-summary-row-added-fade {
          0% { background-color: #fef3c7; }
          100% { background-color: transparent; }
        }
        .drainage-summary-row-added > td {
          animation: drainage-summary-row-added-fade 1.8s ease-out;
        }
        /* Smooth expand/collapse instead of antd's default instant toggle. */
        .drainage-summary-table .ant-table-row-expand-icon {
          transition: transform 0.2s ease;
        }
        .drainage-summary-table .ant-table-tbody > tr {
          transition: background-color 0.3s ease;
        }
      `}</style>

      {(() => {
        // Formula modal for per-plot rows (Foul – OSMA / Storm – OSMA / Foul –
        // Polypipe / Storm – Polypipe). Surfaces the exact `Σ(qty × rate)` /
        // nrPlotsTakeOff math used to derive the displayed Rate. Pulls live
        // values from the matching row in `processedData`, so the numbers
        // always match what the user sees in the table.
        if (!formulaInfoKey) return null;
        let row: any | undefined;
        for (const section of processedData) {
          const found = (section.children ?? []).find((c: any) => c.key === formulaInfoKey);
          if (found) { row = found; break; }
        }
        if (!row || !Array.isArray(row.formulaBreakdown)) return null;
        const breakdown = row.formulaBreakdown as PerPlotFormulaBreakdown[];
        const nonZero = breakdown.filter((b) => b.qty !== 0 && b.rate !== 0);
        const combinedSum = row.formulaCombinedSum as number;
        const nrTakeoff = row.formulaNrPlotsTakeOff as number;
        const totalPlots = row.formulaTotalPlots as number;
        const sectionLabel = row.formulaSectionLabel as string;
        const perPlotRate = nrTakeoff > 0 ? combinedSum / nrTakeoff : 0;
        const totalAmount = totalPlots * parseFloat(perPlotRate.toFixed(2));
        const fmtGBP = (n: number) => n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return (
          <Modal
            title={`${sectionLabel} — Rate Formula`}
            open={true}
            onCancel={() => setFormulaInfoKey(null)}
            footer={[
              <Button key="close" onClick={() => setFormulaInfoKey(null)}>Close</Button>,
            ]}
            width={680}
          >
            <div style={{ fontSize: 13, color: '#374151' }}>
              <p style={{ margin: '0 0 12px' }}>
                Per-plot rate = <code>Σ(qty × rate) ÷ nrPlotsTakeOff</code>,
                summed across every "combined" field in this section. NME PPIC /
                600mm NME PPIC (and on Storm sections, Yard Gully / Drainage
                Channel / Land Drain) are NOT in this sum — they appear as
                their own rows below.
              </p>

              <Text strong style={{ display: 'block', marginBottom: 6 }}>Step 1 — Combined items (qty × rate)</Text>
              <Table
                size="small"
                bordered
                pagination={false}
                dataSource={(nonZero.length > 0 ? nonZero : breakdown).map((b, i) => ({
                  key: `${b.field}-${i}`,
                  label: b.label,
                  qty: b.qty,
                  rate: b.rate,
                  contribution: b.qty * b.rate,
                  field: b.field,
                }))}
                columns={[
                  { title: 'Item', dataIndex: 'label', key: 'label' },
                  { title: 'Qty', dataIndex: 'qty', key: 'qty', align: 'right', render: (v: number) => v.toLocaleString('en-GB') },
                  { title: 'Rate', dataIndex: 'rate', key: 'rate', align: 'right', render: (v: number) => `£${v.toFixed(4)}` },
                  { title: 'Qty × Rate', dataIndex: 'contribution', key: 'contribution', align: 'right', render: (v: number) => `£${fmtGBP(v)}` },
                ]}
                summary={() => (
                  <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 600 }}>
                    <Table.Summary.Cell index={0} colSpan={3} align="right"><Text strong>Sum</Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right"><Text strong>£{fmtGBP(combinedSum)}</Text></Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />

              <div style={{ marginTop: 16, padding: 12, background: '#f6f8fa', borderRadius: 4 }}>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>Step 2 — Divide by Take-off plots</Text>
                <pre style={{ margin: 0, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>
{`Rate per plot = Σ ÷ nrPlotsTakeOff
              = £${fmtGBP(combinedSum)} ÷ ${nrTakeoff}
              = £${fmtGBP(perPlotRate)}`}
                </pre>
              </div>

              <div style={{ marginTop: 12, padding: 12, background: '#f6f8fa', borderRadius: 4 }}>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>Step 3 — Multiply by Total plots</Text>
                <pre style={{ margin: 0, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>
{`Total amount = ratePerPlot × totalPlots
             = £${fmtGBP(parseFloat(perPlotRate.toFixed(2)))} × ${totalPlots.toLocaleString('en-GB')}
             = £${fmtGBP(totalAmount)}`}
                </pre>
              </div>
            </div>
          </Modal>
        );
      })()}

      <Modal
        title="Configure Headers required"
        open={!!configViolationModalRecord}
        onCancel={() => setConfigViolationModalRecord(null)}
        footer={
          <Button type="primary" onClick={() => setConfigViolationModalRecord(null)}>
            OK
          </Button>
        }
      >
        {configViolationModalRecord && (
          <div>
            <Text strong>{configViolationModalRecord.section}</Text>
            <div style={{ marginTop: 8 }}>
              <Text>{configViolationModalRecord.message}</Text>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="SummaryKey — Amount Calculation"
        open={!!summaryKeyModalRecord}
        onCancel={() => setSummaryKeyModalRecord(null)}
        footer={null}
      >
        {summaryKeyModalRecord && (
          <BcCalculationDetails
            record={summaryKeyModalRecord as unknown as BoqSummaryLineItem}
            getDbRate={(code) => codeRateLookup.get(code)?.rate ?? 0}
          />
        )}
      </Modal>

      <Modal
        title={extraSummaryKeyRecord?.persisted ? "SummaryKey — Amount Calculation" : "SummaryKey — Live Value (not yet in BOQ export)"}
        open={!!extraSummaryKeyRecord}
        onCancel={() => setExtraSummaryKeyRecord(null)}
        footer={null}
      >
        {extraSummaryKeyRecord && (() => {
          const { live, persisted } = extraSummaryKeyRecord;
          const bc = computeDynamicBcCalculation(live, (code) => codeRateLookup.get(code)?.rate ?? 0);
          return (
            <>
              <SummaryKeyRawFields record={(persisted ?? live) as unknown as Record<string, unknown>} />
              {bc && (
                <div style={{ marginTop: 8, padding: 8, background: '#eff6ff', borderRadius: 4 }}>
                  <Text strong>BCCalculation (backend dbRate-only derivation):</Text>
                  <Descriptions bordered size="small" column={1} style={{ marginTop: 8 }}>
                    <Descriptions.Item label="Computed Rate (rounded 2dp)">£{bc.rate.toFixed(2)}</Descriptions.Item>
                    <Descriptions.Item label="Quantity (rounded)">
                      {bc.quantity.toLocaleString('en-GB')} {formatUnit(live.unit)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Amount (rounded 2dp)">
                      £{bc.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Descriptions.Item>
                  </Descriptions>
                  <div style={{ marginTop: 8 }}>
                    {bc.steps.map((step, idx) => (
                      <div key={idx}>
                        <Text style={{ fontSize: 12, color: '#374151' }}>{step}</Text>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </Modal>

      {extraItemsApi.modals}
    </div>
  );
};

export default DrainageBillSummary;
